import { PrimeMark } from '@/components/PrimeMark'
import { useEffect, useState } from 'react'
import { Link, useNavigate } from '@tanstack/react-router'
import { BarChart3, Bell, Eye, GitMerge, MoreHorizontal, Network, Plus, Sparkles, X } from 'lucide-react'
import { api, engine } from '@/runtime'
import { useAllRuns, useSettings } from '@/db/hooks'
import type { LeadWatch } from '@/db/api'
import { useNow } from '@/hooks/useNow'
import type { AgentRunResource } from '@/kinds/agentrun'
import { NewAgentSheet, isLead, repoName, useFactoryEnabled, useRunPhase } from '@/kinds/agentrun/components'
import { isOver } from '@/kinds/agentrun/schema'
import { AgentMenu } from '@/components/AgentMenu'
import { AgentsLayout } from '@/components/AgentList'
import { HolderDot, useWaiting } from '@/components/Waiting'
import { RunTag } from '@/components/RunStatus'
import { moving, questionOf, waited, type Waiting } from '@/lib/waiting'
import { Button, Empty, IconButton, PageTitle, SectionTitle, Tag, cx } from '@/components/ui'
import { plainWords } from '@/lib/plainWords'

// The Agents screen (/agents/all): Prime on top, saying whether it watches the
// agents, then every coding agent grouped by who holds its work (you, Prime,
// running: lib/waiting.ts, the same as the Now page), then idle and done,
// each with its last words, its pull request and a ⋯ for everything you can do
// to it. Built for a phone: one column, whole rows to tap, menus not icons.

const ago = (iso: string | undefined, now: number) => {
  if (!iso) return ''
  const m = Math.round((now - Date.parse(iso)) / 60000)
  if (m < 1) return 'now'
  if (m < 60) return `${m}m`
  const h = Math.round(m / 60)
  if (h < 24) return `${h}h`
  return `${Math.round(h / 24)}d`
}

const lastWords = (run: AgentRunResource) => {
  const msgs = run.spec.messages ?? []
  for (let i = msgs.length - 1; i >= 0; i--) {
    const m = msgs[i]
    // plain words: a row clips them to a line, where markdown would be noise
    if (m?.role === 'assistant') return plainWords(m.text)
  }
  return ''
}

const AgentRow = ({
  run,
  watched,
  now,
  wait,
}: {
  run: AgentRunResource
  watched: boolean
  now: number
  wait?: Waiting
}) => {
  const phase = useRunPhase(run)
  const [menu, setMenu] = useState(false)
  const working = phase === 'Working' || phase === 'Starting' || phase === 'Pending'
  const words = lastWords(run)
  return (
    <li className="relative" data-testid="agents-row">
      <Link to="/agents/run/$id" params={{ id: run.id }} className="block px-3 py-3 pr-12">
        <span className="flex items-start gap-2">
          {wait ? (
            <HolderDot holder={wait.holder} className="mt-1.5" />
          ) : (
            <span
              className={cx(
                'mt-1.5 size-2 rounded-full shrink-0',
                working ? 'bg-[var(--color-now)] animate-pulse' : 'bg-fg/25',
              )}
              aria-hidden
            />
          )}
          <span className="flex-1 min-w-0">
            <span className="text-[15px] font-medium leading-snug line-clamp-2">
              {run.spec.title || run.spec.name}
            </span>
            <span className="flex flex-wrap items-center gap-1.5 mt-1 text-xs text-muted">
              {/* who holds it says more than a chip (the why line under it); one chip when nobody does */}
              {!wait && <RunTag run={run} phase={phase} />}
              {run.spec.pr_number ? (
                <Tag tone={run.spec.pr_state === 'merged' ? 'done' : undefined}>
                  PR #{run.spec.pr_number}
                  {run.spec.pr_state === 'merged' ? ' merged' : ''}
                </Tag>
              ) : null}
              <span className="font-mono text-faint truncate">{repoName(run.spec.repo_url)}</span>
              <span className="text-faint">{ago(run.spec.last_active_at || run.spec.updated_at, now)}</span>
              {run.spec.cost?.usd && <span className="text-faint tabular-nums">${run.spec.cost.usd}</span>}
              {run.spec.cost?.turns ? (
                <span className="text-faint tabular-nums">{run.spec.cost.turns} turns</span>
              ) : null}
            </span>
            {wait && (
              <span className="flex items-baseline gap-3 mt-1 text-sm" data-testid="agents-why">
                <span
                  className={cx('flex-1 min-w-0', wait.holder === 'you' ? 'text-accent-text' : 'text-fg/80')}
                >
                  {wait.why}
                </span>
                {wait.since && (
                  <span className="text-xs text-faint tabular-nums">{waited(wait.since, now)}</span>
                )}
              </span>
            )}
            {/* a question it asked is already the line above */}
            {words && !(wait?.holder === 'you' && questionOf(run)) && (
              <span className="text-sm text-muted mt-1 line-clamp-2">{words}</span>
            )}
            {watched && !isOver(phase) && (
              <span
                className="flex items-center gap-1 text-[11px] text-faint mt-1"
                data-testid="agents-watched"
              >
                <Eye size={12} aria-hidden /> Prime is watching
              </span>
            )}
          </span>
        </span>
      </Link>
      <span className="absolute right-1.5 top-2">
        <IconButton label="More" onClick={() => setMenu(true)}>
          <MoreHorizontal size={18} aria-hidden />
        </IconButton>
      </span>
      <AgentMenu run={run} open={menu} onClose={() => setMenu(false)} working={working} />
    </li>
  )
}

const LeadCard = ({ lead, now }: { lead: AgentRunResource; now: number }) => {
  const phase = useRunPhase(lead)
  const [menu, setMenu] = useState(false)
  const settings = useSettings()
  const watching = settings.prime_watches_agents !== false
  const words = lastWords(lead)
  const working = phase === 'Working' || phase === 'Starting' || phase === 'Pending'
  return (
    <div className="card relative mb-5" data-testid="agents-lead">
      <Link to="/agents/run/$id" params={{ id: lead.id }} className="block px-4 py-3 pr-12">
        <span className="flex items-center gap-2">
          <PrimeMark size={16} aria-hidden />
          <span className="font-semibold">Prime</span>
          <RunTag run={lead} phase={phase} />
          <span className="text-xs text-faint ml-auto">
            {ago(lead.spec.last_active_at || lead.spec.updated_at, now)}
          </span>
        </span>
        {words && <span className="text-sm text-muted mt-1.5 line-clamp-3">{words}</span>}
        <span className="flex items-center gap-1 text-xs text-faint mt-2">
          <Eye size={13} aria-hidden />
          {watching
            ? 'Watching your coding agents: it reviews each one when it finishes'
            : 'Not watching the agents (Settings)'}
        </span>
      </Link>
      <LeadWatches />
      <span className="absolute right-1.5 top-2">
        <IconButton label="More" onClick={() => setMenu(true)}>
          <MoreHorizontal size={18} aria-hidden />
        </IconButton>
      </span>
      <AgentMenu run={lead} open={menu} onClose={() => setMenu(false)} working={working} />
    </div>
  )
}

/** Prime's "when … then …" watches, each with a way to cancel it (online only). */
const LeadWatches = () => {
  const [watches, setWatches] = useState<LeadWatch[] | null>(null)
  useEffect(() => {
    let stale = false
    api
      .leadWatches()
      .then((r) => {
        if (!stale) setWatches(r.watches)
      })
      .catch(() => {})
    return () => {
      stale = true
    }
  }, [])
  if (!watches?.length) return null
  const cancel = async (id: string) => {
    setWatches((ws) => (ws ?? []).filter((w) => w.id !== id))
    try {
      await api.leadUnwatch(id)
    } catch {
      // gone already, or offline: the list comes back on the next visit
    }
  }
  return (
    <ul className="border-t border-line/60 px-4 py-2 flex flex-col gap-1" data-testid="lead-watches">
      {watches.map((w) => (
        <li key={w.id} className="flex items-start gap-2 text-xs">
          <Bell size={13} className="mt-0.5 shrink-0 text-muted" aria-hidden />
          <span className="flex-1 min-w-0">
            <span className="text-fg-2">When {w.label}</span>
            <span className="block text-faint line-clamp-2">
              {w.then === 'wake' ? 'Prime will: ' : w.then === 'both' ? 'You and Prime hear: ' : 'You hear: '}
              {w.message}
            </span>
          </span>
          <button
            type="button"
            onClick={() => void cancel(w.id)}
            className="text-faint hover:text-fg min-h-7 px-1"
            aria-label="Cancel this watch"
          >
            <X size={14} aria-hidden />
          </button>
        </li>
      ))}
    </ul>
  )
}

export const AgentsHome = () => {
  const navigate = useNavigate()
  const runs = (useAllRuns() ?? []).filter((r) => r.spec.source.provider === 'factory' && !r.deleted_at)
  const factory = useFactoryEnabled()
  const settings = useSettings()
  const now = useNow(60_000)
  const [starting, setStarting] = useState(false)
  const [leadBusy, setLeadBusy] = useState(false)
  const lead = runs.find((r) => isLead(r) && !isOver(r.spec.status))
  const watched = !!lead && settings.prime_watches_agents !== false
  const coding = runs.filter((r) => !isLead(r))
  // Prime's earlier sessions, read-only, newest first (their threads stay)
  const earlier = runs
    .filter((r) => isLead(r) && isOver(r.spec.status))
    .sort((a, b) => (a.created_at < b.created_at ? 1 : -1))
    .slice(0, 10)
  const weekAgo = new Date(now - 7 * 86400_000).toISOString()
  // grouped by who holds each agent's work (lib/waiting.ts), as on the Now page and Prime's chips
  const { items } = useWaiting()
  const waitFor = new Map(items.filter((w) => w.run).map((w) => [w.run?.id ?? '', w]))
  const held = (h: (w: Waiting) => boolean) =>
    items.filter((w) => w.run && !isLead(w.run) && h(w)).map((w) => w.run as AgentRunResource)
  const needsYou = held((w) => w.holder === 'you')
  const onPrime = held((w) => w.holder === 'prime')
  const going = held((w) => moving(w.holder))
  const idle = coding.filter((r) => !isOver(r.spec.status) && !waitFor.has(r.id))
  const done = coding
    .filter((r) => r.spec.status === 'Ended' && (r.spec.updated_at ?? '') >= weekAgo)
    .slice(0, 10)
  const startLead = async () => {
    setLeadBusy(true)
    try {
      const st = await api.leadStart()
      await engine.sync()
      void navigate({ to: '/agents/run/$id', params: { id: st.run_id } })
    } catch {
      setLeadBusy(false)
    }
  }
  const section = (title: string, list: AgentRunResource[], testid: string) =>
    list.length > 0 && (
      <section className="mb-5" data-testid={testid}>
        <SectionTitle>{title}</SectionTitle>
        <ul className="group-list">
          {list.map((r) => (
            <AgentRow key={r.id} run={r} watched={watched} now={now} wait={waitFor.get(r.id)} />
          ))}
        </ul>
      </section>
    )
  return (
    <AgentsLayout currentId="all">
      <div data-testid="agents-home">
        <PageTitle
          right={
            factory ? (
              <span className="flex items-center gap-1 lg:hidden">
                <IconButton label="Graph" onClick={() => void navigate({ to: '/agents/graph' })}>
                  <Network size={18} aria-hidden />
                </IconButton>
                <IconButton label="Pipeline" onClick={() => void navigate({ to: '/agents/pipeline' })}>
                  <GitMerge size={18} aria-hidden />
                </IconButton>
                <IconButton label="Stats" onClick={() => void navigate({ to: '/agents/stats' })}>
                  <BarChart3 size={18} aria-hidden />
                </IconButton>
                <IconButton label="New coding agent" onClick={() => setStarting(true)}>
                  <Plus size={18} aria-hidden />
                </IconButton>
              </span>
            ) : undefined
          }
        >
          Agents
        </PageTitle>
        {!factory && <Empty>The software factory is not set up on this server.</Empty>}
        {factory && lead && <LeadCard lead={lead} now={now} />}
        {factory && !lead && (
          <div className="card px-4 py-3 mb-5">
            <p className="font-semibold flex items-center gap-2">
              <PrimeMark size={16} aria-hidden /> Prime is not running
            </p>
            <p className="text-sm text-muted mt-1 mb-2">
              Start it to have a lead that plans the work, briefs the coding agents and reviews what they do.
            </p>
            <Button
              variant="chipOn"
              onClick={() => void startLead()}
              disabled={leadBusy}
              data-testid="agents-lead-start"
            >
              {leadBusy ? 'Starting…' : 'Start Prime'}
            </Button>
          </div>
        )}
        {section('On you', needsYou, 'agents-needs-you')}
        {section('On Prime', onPrime, 'agents-on-prime')}
        {section('Running', going, 'agents-working')}
        {section('Idle', idle, 'agents-idle')}
        {section('Done this week', done, 'agents-done')}
        {earlier.length > 0 && (
          <section className="mb-5" data-testid="agents-earlier-prime">
            <SectionTitle>Earlier Prime sessions</SectionTitle>
            <ul className="group-list">
              {earlier.map((r) => (
                <li key={r.id}>
                  <Link
                    to="/agents/run/$id"
                    params={{ id: r.id }}
                    className="flex items-center gap-2 px-3 py-2 min-h-11 text-sm"
                  >
                    <Sparkles size={14} aria-hidden className="text-muted" />
                    <span className="flex-1 min-w-0 truncate">
                      Started{' '}
                      {new Date(r.created_at).toLocaleDateString(undefined, {
                        day: 'numeric',
                        month: 'short',
                      })}
                    </span>
                    {r.spec.cost?.turns ? (
                      <span className="text-xs text-faint">{r.spec.cost.turns} turns</span>
                    ) : null}
                    <span className="text-xs text-faint">
                      {ago(r.spec.last_active_at || r.spec.updated_at, now)}
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          </section>
        )}
        {factory && coding.length === 0 && (
          <p className="text-sm text-muted">
            No coding agents yet. Ask Prime for something, or start one with +.
          </p>
        )}
        {starting && <NewAgentSheet onClose={() => setStarting(false)} />}
      </div>
    </AgentsLayout>
  )
}
